import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  X, 
  ArrowRight, 
  Bot, 
  Sparkles, 
  FileText, 
  Mic,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface VisitorStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export const HomepageWelcomeTour = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const steps: VisitorStep[] = [
    {
      id: 'intro',
      title: 'Olá! Sou sua consultora 👋',
      description: 'Sou especializada em ajudar pequenos e microempreendedores a tomar decisões mais seguras para seus negócios.',
      icon: <Bot className="w-8 h-8" />
    },
    {
      id: 'what',
      title: 'Consultoria Especializada',
      description: 'Orientação profissional em gestão, marketing, finanças e legislação. Tudo personalizado para a realidade do seu negócio.',
      icon: <Sparkles className="w-8 h-8" />
    },
    {
      id: 'documents',
      title: 'Análise de Documentos',
      description: 'Envie relatórios, planilhas ou contratos e receba uma análise detalhada com recomendações práticas.',
      icon: <FileText className="w-8 h-8" />
    },
    {
      id: 'voice',
      title: 'Consulta por Voz',
      description: 'Converse comigo como se estivesse ligando para uma consultora. Disponível nos planos pagos.',
      icon: <Mic className="w-8 h-8" />
    },
    {
      id: 'results',
      title: 'Resultados Reais',
      description: 'Mais de 127 empreendedores já usam a AI4ME para decisões mais seguras. Quer conhecer?',
      icon: <TrendingUp className="w-8 h-8" />
    }
  ];

  useEffect(() => {
    // Verificar se já viu o tour
    const seen = localStorage.getItem('homepage_tour_seen');
    if (seen) return;

    // Mostrar após 8 segundos na página
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('homepage_tour_seen', 'true');
    setIsVisible(false);
  };

  const handleComplete = () => {
    localStorage.setItem('homepage_tour_seen', 'true');
    setIsVisible(false);
    navigate('/auth');
  };

  const handleTryNow = () => {
    localStorage.setItem('homepage_tour_seen', 'true');
    setIsVisible(false);
    navigate('/auth');
  };

  if (!isVisible) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleSkip}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
        >
          <Card className="border-2 border-primary/50 shadow-2xl overflow-hidden">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-primary-foreground">
              <div className="flex items-center justify-between">
                <motion.div 
                  className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {step.icon}
                </motion.div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  className="text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <h2 className="text-2xl font-bold mt-4">{step.title}</h2>
            </div>

            <CardContent className="p-6">
              <p className="text-muted-foreground mb-6 leading-relaxed text-base">
                {step.description}
              </p>

              {/* Progress dots */}
              <div className="flex gap-2 justify-center mb-6">
                {steps.map((_, index) => (
                  <motion.div
                    key={index}
                    className={`h-2 rounded-full transition-all ${
                      index === currentStep 
                        ? 'w-8 bg-primary' 
                        : index < currentStep 
                          ? 'w-2 bg-primary/60' 
                          : 'w-2 bg-muted'
                    }`}
                  />
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleSkip}
                >
                  Agora não
                </Button>
                
                {isLastStep ? (
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={handleTryNow}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Experimentar Grátis
                  </Button>
                ) : (
                  <Button
                    className="flex-1"
                    onClick={handleNext}
                  >
                    Próximo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>

              {/* Quick CTA */}
              {!isLastStep && (
                <div className="mt-4 text-center">
                  <Button 
                    variant="link" 
                    className="text-sm text-primary"
                    onClick={handleTryNow}
                  >
                    Pular e criar conta grátis →
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
